import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  strapiGetLaboratoryWorkOrderForm,
  strapiReviewLaboratoryWorkOrderParameter,
  type LaboratoryResultBeritaAcaraEntry,
  type LaboratoryResultParameterReviewStatus,
} from '../../../api/strapiCrm';
import { useAuth } from '../../../auth/AuthContext';
import { ChemInline } from '../../../components/ChemInline';
import { queryKeys } from '../../../query/queryKeys';

export type HasilUjiLetterViewDialogProps = {
  open: boolean;
  workOrderDocumentId: string;
  jobNumber?: string;
  /** SPV Admin Sampling: lihat hasil saja, tanpa approve/reject/edit parameter. */
  viewOnly?: boolean;
  /** SPV Admin Sampling: tampilkan nilai parameter lapangan di samping hasil uji. */
  showFieldParameter?: boolean;
  onClose: () => void;
};

type EditingKey = string;
type ParamRow = LaboratoryResultBeritaAcaraEntry['parameters'][number];

type ReviewPayload = {
  berita_acara_document_id: string;
  parameter_uji_document_id: string;
  action: 'update_result' | 'approve' | 'reject';
  result?: string;
  result_corrected?: string;
  review_note?: string;
};

function paramKey(baDocId: string, paramDocId: string): EditingKey {
  return `${baDocId}::${paramDocId}`;
}

function reviewChipColor(
  status: LaboratoryResultParameterReviewStatus,
): 'default' | 'success' | 'error' | 'warning' {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  return 'warning';
}

function reviewChipLabel(status: LaboratoryResultParameterReviewStatus): string {
  if (status === 'approved') return 'Approved';
  if (status === 'rejected') return 'Rejected';
  return 'Pending';
}

function matchesParameterFilter(p: ParamRow, filter: string): boolean {
  const q = filter.trim().toLowerCase();
  if (!q) return true;
  const name = (p.parameter_name ?? '').toLowerCase();
  const simbol = (p.parameter_simbol ?? '').toLowerCase();
  return name.includes(q) || simbol.includes(q);
}

function canReviewParam(p: ParamRow): boolean {
  return Boolean((p.result ?? '').trim());
}

function payloadsSuccessMessage(
  n: number,
  singular: string,
  plural: (n: number) => string,
): string {
  return n > 1 ? plural(n) : singular;
}

export function HasilUjiLetterViewDialog({
  open,
  workOrderDocumentId,
  jobNumber,
  viewOnly = false,
  showFieldParameter = false,
  onClose,
}: HasilUjiLetterViewDialogProps) {
  const { jwt } = useAuth();
  const queryClient = useQueryClient();
  const woDocId = workOrderDocumentId.trim();

  const [titikTab, setTitikTab] = useState(0);
  const [parameterFilter, setParameterFilter] = useState('');
  const [selectedParamIds, setSelectedParamIds] = useState<Set<string>>(() => new Set());
  const [editingKey, setEditingKey] = useState<EditingKey | null>(null);
  const [draftResult, setDraftResult] = useState('');
  const [draftCorrected, setDraftCorrected] = useState('');
  const [rejectDialog, setRejectDialog] = useState<{
    mode: 'single' | 'bulk';
    baDocId: string;
    items: { paramId: string; paramName: string }[];
  } | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const formQ = useQuery({
    queryKey: queryKeys.laboratoryResultWorkOrderForm(jwt, woDocId),
    queryFn: () => strapiGetLaboratoryWorkOrderForm(jwt!, woDocId),
    enabled: Boolean(open && jwt && woDocId),
  });

  const titikList = useMemo(() => {
    const list = [...(formQ.data?.berita_acara ?? [])];
    list.sort((a, b) => a.point_index - b.point_index || a.point_label.localeCompare(b.point_label, 'id'));
    return list;
  }, [formQ.data?.berita_acara]);

  useEffect(() => {
    if (!open) {
      setTitikTab(0);
      setParameterFilter('');
      setSelectedParamIds(new Set());
      setEditingKey(null);
      setDraftResult('');
      setDraftCorrected('');
      setRejectDialog(null);
      setRejectNote('');
      setActionError(null);
      setActionSuccess(null);
      return;
    }
    if (titikTab >= titikList.length) setTitikTab(0);
  }, [open, titikList.length, titikTab]);

  const activeBa = titikList[titikTab] ?? null;

  const filteredParams = useMemo(() => {
    if (!activeBa) return [];
    return activeBa.parameters.filter((p) => matchesParameterFilter(p, parameterFilter));
  }, [activeBa, parameterFilter]);

  const selectableFiltered = useMemo(
    () => filteredParams.filter(canReviewParam),
    [filteredParams],
  );

  const allFilteredSelected =
    selectableFiltered.length > 0 &&
    selectableFiltered.every((p) => selectedParamIds.has(p.parameter_uji_document_id));

  const someFilteredSelected =
    selectableFiltered.some((p) => selectedParamIds.has(p.parameter_uji_document_id)) &&
    !allFilteredSelected;

  const selectedOnTitik = useMemo(() => {
    if (!activeBa) return [];
    return activeBa.parameters.filter(
      (p) => selectedParamIds.has(p.parameter_uji_document_id) && canReviewParam(p),
    );
  }, [activeBa, selectedParamIds]);

  const invalidateAfterReview = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.laboratoryResultWorkOrderForm(jwt, woDocId),
    });
    await queryClient.invalidateQueries({ queryKey: queryKeys.laboratoryResultGridRowsPrefix(jwt) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.invoiceGridRows(jwt) });
  }, [jwt, queryClient, woDocId]);

  const reviewM = useMutation({
    mutationFn: async (payloads: ReviewPayload[]) => {
      if (!jwt || !woDocId) throw new Error('Sesi tidak valid.');
      const errors: string[] = [];
      const results: Awaited<ReturnType<typeof strapiReviewLaboratoryWorkOrderParameter>>[] = [];
      for (const payload of payloads) {
        try {
          results.push(await strapiReviewLaboratoryWorkOrderParameter(jwt, woDocId, payload));
        } catch (e) {
          errors.push(e instanceof Error ? e.message : 'Gagal');
        }
      }
      if (errors.length) {
        throw new Error(
          payloads.length === 1
            ? errors[0]
            : `${errors.length}/${payloads.length} gagal. ${errors[0]}`,
        );
      }
      return { payloads, results };
    },
    onSuccess: async ({ payloads, results }) => {
      setActionError(null);
      const action = payloads[0]?.action;
      const n = payloads.length;
      if (action === 'update_result') {
        setActionSuccess('Hasil parameter diperbarui.');
      } else if (action === 'approve') {
        setActionSuccess(
          payloadsSuccessMessage(n, 'Parameter di-approve.', (count) => `${count} parameter di-approve.`),
        );
      } else if (results.some((d) => d.request_edit_triggered)) {
        setActionSuccess(
          'Parameter di-reject. Work order dikembalikan ke Pengujian (request edit) agar analis memperbaiki.',
        );
      } else {
        setActionSuccess(
          payloadsSuccessMessage(n, 'Parameter di-reject.', (count) => `${count} parameter di-reject.`),
        );
      }
      setEditingKey(null);
      setDraftResult('');
      setDraftCorrected('');
      setRejectDialog(null);
      setRejectNote('');
      setSelectedParamIds(new Set());
      await invalidateAfterReview();
    },
    onError: (e) => {
      setActionSuccess(null);
      setActionError(e instanceof Error ? e.message : 'Gagal menjalankan aksi parameter.');
    },
  });

  const startEdit = useCallback(
    (baDocId: string, paramDocId: string, current: string, corrected: string) => {
      setActionError(null);
      setActionSuccess(null);
      setEditingKey(paramKey(baDocId, paramDocId));
      setDraftResult(current);
      setDraftCorrected(corrected);
    },
    [],
  );

  const toggleParam = useCallback((paramId: string, checked: boolean) => {
    setSelectedParamIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(paramId);
      else next.delete(paramId);
      return next;
    });
  }, []);

  const toggleSelectAllFiltered = useCallback(
    (checked: boolean) => {
      setSelectedParamIds((prev) => {
        const next = new Set(prev);
        for (const p of selectableFiltered) {
          if (checked) next.add(p.parameter_uji_document_id);
          else next.delete(p.parameter_uji_document_id);
        }
        return next;
      });
    },
    [selectableFiltered],
  );

  const title =
    jobNumber?.trim() ||
    formQ.data?.work_order_job_number ||
    woDocId ||
    'View hasil lab';

  const busy = reviewM.isPending;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (!busy) onClose();
        }}
        fullWidth
        maxWidth="md"
        data-testid="hasil-uji-letter-view-dialog"
      >
        <DialogTitle>
          View hasil lab — {title}
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.5 }}>
            {viewOnly
              ? 'Mode lihat saja. Approve LHU dilakukan di grid Hasil Uji Analis.'
              : 'Satu tab per titik. Centang parameter untuk bulk approve/reject pada titik aktif.'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5 }}>
          {formQ.isPending ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : null}
          {formQ.isError ? (
            <Alert severity="error">
              {formQ.error instanceof Error ? formQ.error.message : 'Gagal memuat hasil lab.'}
            </Alert>
          ) : null}
          {actionError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          ) : null}
          {actionSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(null)}>
              {actionSuccess}
            </Alert>
          ) : null}

          {!formQ.isPending && !formQ.isError && titikList.length === 0 ? (
            <Alert severity="info">Belum ada berita acara / hasil uji untuk ditampilkan.</Alert>
          ) : null}

          {titikList.length > 0 ? (
            <Stack spacing={1.5}>
              <TextField
                size="small"
                label="Filter nama parameter"
                placeholder="Cari parameter…"
                value={parameterFilter}
                onChange={(e) => setParameterFilter(e.target.value)}
                inputProps={{ 'data-testid': 'hasil-uji-letter-view-parameter-filter' }}
              />

              {titikList.length > 1 ? (
                <Tabs
                  value={titikTab}
                  onChange={(_e, v: number) => {
                    setTitikTab(v);
                    setSelectedParamIds(new Set());
                    setEditingKey(null);
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                >
                  {titikList.map((ba) => (
                    <Tab
                      key={ba.berita_acara_document_id}
                      label={ba.point_label || ba.row_label || 'Titik'}
                      id={`hasil-uji-titik-tab-${ba.berita_acara_document_id}`}
                    />
                  ))}
                </Tabs>
              ) : null}

              {activeBa ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {activeBa.point_label}
                    {activeBa.location_name ? ` · ${activeBa.location_name}` : ''}
                    {activeBa.bidang_label ? ` · ${activeBa.bidang_label}` : ''}
                  </Typography>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    {!viewOnly ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={allFilteredSelected}
                            indeterminate={someFilteredSelected}
                            disabled={busy || selectableFiltered.length === 0}
                            onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                            inputProps={{
                              'aria-label': 'Pilih semua parameter terfilter pada titik ini',
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Pilih semua{parameterFilter.trim() ? ' (terfilter)' : ''} (
                            {selectableFiltered.length})
                          </Typography>
                        }
                      />
                    ) : (
                      <Box />
                    )}
                    {!viewOnly ? (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<CheckCircleOutlineIcon />}
                        disabled={busy || selectedOnTitik.length === 0}
                        onClick={() => {
                          if (!activeBa || selectedOnTitik.length === 0) return;
                          setActionError(null);
                          setActionSuccess(null);
                          reviewM.mutate(
                            selectedOnTitik.map((p) => ({
                              berita_acara_document_id: activeBa.berita_acara_document_id,
                              parameter_uji_document_id: p.parameter_uji_document_id,
                              action: 'approve' as const,
                            })),
                          );
                        }}
                        data-testid="hasil-uji-letter-bulk-approve"
                      >
                        Approve ({selectedOnTitik.length})
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelOutlinedIcon />}
                        disabled={busy || selectedOnTitik.length === 0}
                        onClick={() => {
                          if (!activeBa || selectedOnTitik.length === 0) return;
                          setActionError(null);
                          setActionSuccess(null);
                          setRejectDialog({
                            mode: 'bulk',
                            baDocId: activeBa.berita_acara_document_id,
                            items: selectedOnTitik.map((p) => ({
                              paramId: p.parameter_uji_document_id,
                              paramName: p.parameter_name,
                            })),
                          });
                          setRejectNote('');
                        }}
                        data-testid="hasil-uji-letter-bulk-reject"
                      >
                        Reject ({selectedOnTitik.length})
                      </Button>
                    </Stack>
                    ) : null}
                  </Stack>

                  {filteredParams.length === 0 ? (
                    <Alert severity="info">
                      Tidak ada parameter yang cocok dengan filter pada titik ini.
                    </Alert>
                  ) : (
                    <Stack spacing={1.5}>
                      {filteredParams.map((p) => {
                        const key = paramKey(
                          activeBa.berita_acara_document_id,
                          p.parameter_uji_document_id,
                        );
                        const isEditing = !viewOnly && editingKey === key;
                        const reviewStatus = p.review_status ?? 'pending';
                        const reviewable = canReviewParam(p);
                        const showCorrected = Boolean(activeBa.has_emission_correction);
                        const showLapangan = Boolean(showFieldParameter && p.is_parameter_lapangan);
                        const analisLabel = (p.analis_types ?? []).filter(Boolean).join(', ') || '—';
                        const smColumns = viewOnly
                          ? ['minmax(0, 1fr)', 'minmax(0, 1fr)']
                          : ['auto', 'minmax(0, 1fr)', 'minmax(0, 1fr)'];
                        if (showLapangan) smColumns.push('minmax(0, 1fr)');
                        if (showCorrected) smColumns.push('minmax(0, 1fr)');
                        if (!viewOnly) smColumns.push('auto');
                        return (
                          <Box
                            key={p.parameter_uji_document_id}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: viewOnly ? '1fr' : 'auto 1fr',
                                sm: smColumns.join(' '),
                              },
                              gap: 1.5,
                              alignItems: 'center',
                            }}
                          >
                            {!viewOnly ? (
                              <Checkbox
                                size="small"
                                checked={selectedParamIds.has(p.parameter_uji_document_id)}
                                disabled={busy || !reviewable || isEditing}
                                onChange={(e) =>
                                  toggleParam(p.parameter_uji_document_id, e.target.checked)
                                }
                                inputProps={{
                                  'aria-label': `Pilih ${p.parameter_name}`,
                                }}
                              />
                            ) : null}
                            <Box>
                              <Typography variant="body2">
                                <ChemInline text={p.parameter_name} />
                                {(p.parameter_simbol ?? '').trim() ? (
                                  <>
                                    {' '}
                                    (<ChemInline text={p.parameter_simbol ?? ''} />)
                                  </>
                                ) : null}
                              </Typography>
                              <Chip
                                size="small"
                                sx={{ mt: 0.5, mr: 0.5 }}
                                color={reviewChipColor(reviewStatus)}
                                label={reviewChipLabel(reviewStatus)}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                                label={`Analis: ${analisLabel}`}
                                data-testid={`hasil-uji-letter-analis-type-${p.parameter_uji_document_id}`}
                              />
                            </Box>
                            <TextField
                              variant="standard"
                              size="small"
                              fullWidth
                              label="Hasil uji"
                              placeholder="—"
                              value={isEditing ? draftResult : (p.result ?? '')}
                              disabled={viewOnly || !isEditing || busy}
                              onChange={(e) => setDraftResult(e.target.value)}
                              sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                              inputProps={{
                                'data-testid': `hasil-uji-letter-view-result-${p.parameter_uji_document_id}`,
                              }}
                            />
                            {showLapangan ? (
                              <TextField
                                variant="standard"
                                size="small"
                                fullWidth
                                label="Parameter lapangan"
                                placeholder="—"
                                value={p.parameter_lapangan_value ?? ''}
                                disabled
                                sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                                inputProps={{
                                  'data-testid': `hasil-uji-letter-view-field-parameter-${p.parameter_uji_document_id}`,
                                  readOnly: true,
                                }}
                              />
                            ) : null}
                            {showCorrected ? (
                              <TextField
                                variant="standard"
                                size="small"
                                fullWidth
                                label="Corrected result"
                                placeholder="—"
                                value={isEditing ? draftCorrected : (p.result_corrected ?? '')}
                                disabled={viewOnly || !isEditing || busy}
                                onChange={(e) => setDraftCorrected(e.target.value)}
                                sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                                inputProps={{
                                  'data-testid': `hasil-uji-letter-view-result-corrected-${p.parameter_uji_document_id}`,
                                }}
                              />
                            ) : null}
                            {!viewOnly ? (
                            <Stack
                              direction="row"
                              spacing={0.25}
                              justifyContent="flex-end"
                              sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                            >
                              {isEditing ? (
                                <Tooltip title="Simpan hasil">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      disabled={busy || !draftResult.trim()}
                                      aria-label={`Simpan ${p.parameter_name}`}
                                      onClick={() => {
                                        reviewM.mutate([
                                          {
                                            berita_acara_document_id:
                                              activeBa.berita_acara_document_id,
                                            parameter_uji_document_id: p.parameter_uji_document_id,
                                            action: 'update_result',
                                            result: draftResult.trim(),
                                            ...(showCorrected
                                              ? { result_corrected: draftCorrected.trim() }
                                              : {}),
                                          },
                                        ]);
                                      }}
                                    >
                                      <SaveOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Edit hasil">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={busy}
                                      aria-label={`Edit ${p.parameter_name}`}
                                      data-testid={`hasil-uji-letter-edit-${p.parameter_uji_document_id}`}
                                      onClick={() =>
                                        startEdit(
                                          activeBa.berita_acara_document_id,
                                          p.parameter_uji_document_id,
                                          p.result ?? '',
                                          p.result_corrected ?? '',
                                        )
                                      }
                                    >
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              <Tooltip title="Approve parameter">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={busy || isEditing || !reviewable}
                                    aria-label={`Approve ${p.parameter_name}`}
                                    data-testid={`hasil-uji-letter-approve-param-${p.parameter_uji_document_id}`}
                                    onClick={() => {
                                      reviewM.mutate([
                                        {
                                          berita_acara_document_id:
                                            activeBa.berita_acara_document_id,
                                          parameter_uji_document_id: p.parameter_uji_document_id,
                                          action: 'approve',
                                        },
                                      ]);
                                    }}
                                  >
                                    <CheckCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Reject parameter">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={busy || isEditing || !reviewable}
                                    aria-label={`Reject ${p.parameter_name}`}
                                    data-testid={`hasil-uji-letter-reject-param-${p.parameter_uji_document_id}`}
                                    onClick={() => {
                                      setActionError(null);
                                      setActionSuccess(null);
                                      setRejectDialog({
                                        mode: 'single',
                                        baDocId: activeBa.berita_acara_document_id,
                                        items: [
                                          {
                                            paramId: p.parameter_uji_document_id,
                                            paramName: p.parameter_name,
                                          },
                                        ],
                                      });
                                      setRejectNote('');
                                    }}
                                  >
                                    <CancelOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                            ) : null}
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={busy}>
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(rejectDialog)}
        onClose={() => {
          if (!busy) setRejectDialog(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {rejectDialog?.mode === 'bulk' ? 'Reject parameter terpilih?' : 'Reject parameter?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {rejectDialog?.mode === 'bulk'
              ? `Tolak ${rejectDialog.items.length} parameter pada titik ini?`
              : (
                  <>
                    Tolak nilai untuk <strong>{rejectDialog?.items[0]?.paramName}</strong>?
                  </>
                )}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Catatan (opsional)"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setRejectDialog(null)}>
            Batal
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy || !rejectDialog?.items.length}
            onClick={() => {
              if (!rejectDialog) return;
              const note = rejectNote.trim() || undefined;
              reviewM.mutate(
                rejectDialog.items.map((item) => ({
                  berita_acara_document_id: rejectDialog.baDocId,
                  parameter_uji_document_id: item.paramId,
                  action: 'reject' as const,
                  review_note: note,
                })),
              );
            }}
          >
            {busy ? 'Memproses…' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
